import { useCallback } from 'react';
import { AdaptoActions } from '@frontend/adapto-actions';
import { Icon } from '@frontend/icons';
import { useScopedAppFlagStore } from '@frontend/scope';
import { ContactActionPrefixes } from '@frontend/tracking-prefixes';
import { ActionButton, PopoverMenu, usePopoverMenu } from '@frontend/design-system';
import { PhoneNumberPopover } from './phone-number-popover';
import { CallSelectionContext } from './types';
import { usePhoneCallAction } from './use-call-action';

export type CallButtonProps = {
  label?: string;
  context: CallSelectionContext;
  trackingIdSuffix?: string;
};

export const CallButton = ({ label, context, trackingIdSuffix }: CallButtonProps) => {
  const { handleCallActionClick, disabled, disabledDetails } = usePhoneCallAction({
    context,
  });

  return (
    <div>
      <ActionButton
        onClick={handleCallActionClick}
        trackingId={`${ContactActionPrefixes.Call}:${trackingIdSuffix}`}
        disabled={disabled}
        label={disabledDetails ? disabledDetails : label}
      >
        <Icon name='phone-call-small' />
      </ActionButton>
    </div>
  );
};

export type CallActionProps = {
  label: string;
  context: CallSelectionContext;
  trackingIdSuffix?: string;
  onClick?: () => void; // Optional onClick handler for additional functionality
  showPopover?: boolean;
};

export const CallAction = ({ label, context, trackingIdSuffix, onClick, showPopover = true }: CallActionProps) => {
  const { handleCallActionClick, disabled, disabledDetails, multipleContactNumbers } = usePhoneCallAction({
    context,
  });

  const { getItemProps, getTriggerProps, getMenuProps, isOpen, open } = usePopoverMenu({
    placement: 'bottom-end',
    omitSizeMiddleware: true,
  });

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const clickToCallFeatureFlag = getFeatureFlagValue('click-to-call-multiple-numbers');

  const hasMultipleNumbers = multipleContactNumbers.length > 1 && clickToCallFeatureFlag;
  const actionTriggerProps = hasMultipleNumbers ? { ...getTriggerProps() } : {};

  const handleClick = useCallback(() => {
    if (hasMultipleNumbers && showPopover) open();
    else handleCallActionClick();
    onClick?.();
  }, [onClick, open, hasMultipleNumbers, handleCallActionClick]);

  return (
    <>
      <div>
        <AdaptoActions.Action
          {...actionTriggerProps}
          onClick={handleClick}
          trackingId={`${ContactActionPrefixes.Call}:${trackingIdSuffix}`}
          disabled={disabled}
          icon='phone'
          label={disabledDetails ? disabledDetails : label}
        />
        {isOpen && (
          <PopoverMenu {...getMenuProps()}>
            <PhoneNumberPopover
              getItemProps={getItemProps}
              trackingIdSuffix={trackingIdSuffix}
              data={multipleContactNumbers}
              onClick={handleCallActionClick ?? (() => {})}
            />
          </PopoverMenu>
        )}
      </div>
    </>
  );
};
